import { Injectable, NgZone, signal } from '@angular/core';
import { NotificationHandlerService } from 'src/app/core/services/notification/notification-handler.service';
import { NotificationSeverity } from 'src/app/core/services/notification/formatters/notification-formatter.interface';

export type VoiceInputState = 'idle' | 'recording' | 'transcribing' | 'error';

/**
 * Sprachaufnahme fuer den Hilfe-Chat (WhatsApp-Stil): Mikro halten -> aufnehmen,
 * loslassen -> Blob liefern. Reine Web-APIs (getUserMedia + MediaRecorder); in der
 * Capacitor-WebView granted Capacitor 7 die RECORD_AUDIO-Permission automatisch
 * (BridgeWebChromeClient), daher kein natives Plugin noetig.
 *
 * Zusaetzlich ein Live-Pegel (`levels`) ueber die Web-Audio-API (AnalyserNode):
 * waehrend der Aufnahme wird der Frequenz-Pegel des Mikrofons in N Balken-Werte
 * (0..1) gemappt -> die Footer-UI zeichnet daraus die WhatsApp-artige Wellenform.
 *
 * Fehler werden ueber den zentralen NotificationHandler als Toast gezeigt (Code =
 * i18n-Key, DEFAULT-Formatter uebersetzt ihn). Die Transkription selbst macht der
 * HelpChatService gegen das Backend — dieser Service kuemmert sich nur um die Aufnahme.
 */
@Injectable({ providedIn: 'root' })
export class VoiceInputService {
  /** Anzahl der Wellenform-Balken (muss zur CSS-Breite passen). */
  static readonly BAR_COUNT = 28;

  private readonly _state = signal<VoiceInputState>('idle');
  /** Reaktiver Aufnahme-Zustand (signal, OnPush-freundlich). */
  readonly state = this._state.asReadonly();

  private readonly _levels = signal<number[]>(new Array(VoiceInputService.BAR_COUNT).fill(0));
  /** Live-Pegel je Balken (0..1) waehrend der Aufnahme; sonst Nullen. */
  readonly levels = this._levels.asReadonly();

  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private mimeType = 'audio/webm';
  private fileName = 'voice.webm';

  // Web-Audio-Pegelmessung.
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private freqData: Uint8Array<ArrayBuffer> | null = null;
  private rafId: number | null = null;
  private lastMeterAt = 0;

  constructor(
    private notifications: NotificationHandlerService,
    private zone: NgZone,
  ) {}

  get isSupported(): boolean {
    return !!navigator?.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
  }

  /**
   * Fordert Mikrofon an und startet die Aufnahme. true = laeuft jetzt.
   * Loest bei Verweigerung/Unsupported selbst einen Toast aus.
   */
  async startRecording(): Promise<boolean> {
    if (this._state() !== 'idle') return false;
    if (!this.isSupported) {
      this.fail('VOICE_ERROR_UNSUPPORTED');
      return false;
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    } catch (err: unknown) {
      this.cleanup();
      const name = (err as { name?: string })?.name;
      this.fail(name === 'NotAllowedError' || name === 'SecurityError'
        ? 'VOICE_ERROR_PERMISSION'
        : 'VOICE_ERROR_UNSUPPORTED');
      return false;
    }

    this.pickMimeType();
    this.chunks = [];
    try {
      this.mediaRecorder = this.mimeType
        ? new MediaRecorder(this.stream, { mimeType: this.mimeType })
        : new MediaRecorder(this.stream);
    } catch {
      // Manche WebViews akzeptieren den expliziten mimeType nicht -> Default.
      this.mediaRecorder = new MediaRecorder(this.stream);
      this.mimeType = this.mediaRecorder.mimeType || 'audio/webm';
    }

    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.mediaRecorder.start();
    this.startMeter();
    this._state.set('recording');
    return true;
  }

  /**
   * Stoppt die Aufnahme und liefert das Audio. State -> 'transcribing'.
   * Liefert null, wenn nichts aufgenommen wurde.
   */
  stopRecording(): Promise<{ blob: Blob; fileName: string } | null> {
    return new Promise((resolve) => {
      const rec = this.mediaRecorder;
      if (!rec || this._state() !== 'recording') {
        this.cancel();
        resolve(null);
        return;
      }

      this.stopMeter();

      rec.onstop = () => {
        const blob = new Blob(this.chunks, { type: this.mimeType || 'audio/webm' });
        const fileName = this.fileName;
        this.releaseStream();
        this.mediaRecorder = null;
        this.chunks = [];
        this._state.set('transcribing');
        resolve(blob.size > 0 ? { blob, fileName } : null);
      };

      try {
        rec.stop();
      } catch {
        this.cancel();
        resolve(null);
      }
    });
  }

  /** Laufende Aufnahme verwerfen (Abbruch / Slide-to-cancel) und Mic freigeben. */
  cancel(): void {
    const rec = this.mediaRecorder;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.onstop = null;
        rec.stop();
      } catch {
        /* ignore */
      }
    }
    this.cleanup();
  }

  /** Nach Transkription oder Fehler zurueck auf 'idle'. */
  finish(): void {
    this._state.set('idle');
  }

  /** Kurzer Hinweis „Halten zum Aufnehmen" bei zu kurzem Tap. */
  notifyHoldHint(): void {
    this.notifications.processNotifications([
      { code: 'HELP_CHAT.VOICE_MIC_HOLD_HINT', severity: NotificationSeverity.Info },
    ]);
  }

  // ===== Pegelmessung (Wellenform) =====

  private startMeter(): void {
    if (!this.stream) return;
    try {
      const Ctx: typeof AudioContext =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      this.audioCtx = new Ctx();
      this.audioCtx.resume?.().catch(() => undefined);
      this.sourceNode = this.audioCtx.createMediaStreamSource(this.stream);
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 64; // -> 32 Frequenz-Bins
      this.analyser.smoothingTimeConstant = 0.75;
      this.sourceNode.connect(this.analyser);
      this.freqData = new Uint8Array(new ArrayBuffer(this.analyser.frequencyBinCount));

      // Ausserhalb der Angular-Zone: der rAF-Loop soll NICHT bei jedem Frame
      // app-weite Change-Detection ausloesen. Die `_levels`-Signal-Mutation
      // markiert die lesende Komponente trotzdem korrekt zum Neuzeichnen.
      this.zone.runOutsideAngular(() => {
        const tick = (ts: number) => {
          if (!this.analyser || !this.freqData) return;
          // ~33 fps reicht fuer eine fluessige Wellenform und spart Last.
          if (ts - this.lastMeterAt >= 30) {
            this.lastMeterAt = ts;
            this.analyser.getByteFrequencyData(this.freqData);
            this._levels.set(this.computeBars(this.freqData));
          }
          this.rafId = requestAnimationFrame(tick);
        };
        this.rafId = requestAnimationFrame(tick);
      });
    } catch {
      // Pegelmessung ist rein dekorativ — bei Problemen still ohne Wellenform aufnehmen.
      this.teardownMeter();
    }
  }

  /** Mappt die FFT-Bins auf BAR_COUNT Balken-Werte (0..1) mit kleinem Grundpegel. */
  private computeBars(data: Uint8Array<ArrayBuffer>): number[] {
    const n = VoiceInputService.BAR_COUNT;
    const bins = data.length;
    const bars = new Array<number>(n);
    for (let i = 0; i < n; i++) {
      // Bin 0 (DC) ueberspringen, Rest gleichmaessig auf die Balken verteilen.
      const idx = 1 + Math.floor((i / n) * (bins - 1));
      const norm = data[idx] / 255; // 0..1
      // Leichte Anhebung + Mindesthoehe, damit die Welle „lebt".
      bars[i] = Math.min(1, 0.08 + Math.pow(norm, 0.7) * 1.05);
    }
    return bars;
  }

  private stopMeter(): void {
    this.teardownMeter();
    this._levels.set(new Array(VoiceInputService.BAR_COUNT).fill(0));
  }

  private teardownMeter(): void {
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    try {
      this.sourceNode?.disconnect();
      this.analyser?.disconnect();
    } catch {
      /* ignore */
    }
    this.sourceNode = null;
    this.analyser = null;
    this.freqData = null;
    if (this.audioCtx) {
      this.audioCtx.close().catch(() => undefined);
      this.audioCtx = null;
    }
  }

  private pickMimeType(): void {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac', 'audio/ogg'];
    const supported = candidates.find((t) => MediaRecorder.isTypeSupported?.(t)) ?? '';
    this.mimeType = supported;
    this.fileName = supported.includes('mp4') || supported.includes('aac')
      ? 'voice.m4a'
      : supported.includes('ogg')
        ? 'voice.ogg'
        : 'voice.webm';
  }

  private fail(key: string): void {
    this.notifications.processNotifications([
      { code: `HELP_CHAT.${key}`, severity: NotificationSeverity.Error },
    ]);
    this._state.set('idle');
  }

  private releaseStream(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  private cleanup(): void {
    this.stopMeter();
    this.releaseStream();
    this.mediaRecorder = null;
    this.chunks = [];
    this._state.set('idle');
  }
}
